/**
 * UNA FINESTRA CHE SI COMPORTA DA FINESTRA.
 *
 * Il foglio di Condividi e l'anteprima di stampa si aprono sopra il ticket:
 * chi usa la tastiera o un lettore di schermo deve trovarcisi dentro, non
 * restare sul ticket dietro. Quindi:
 *   - all'apertura il fuoco va al primo controllo della finestra;
 *   - finche' e' aperta, Tab e Maiuscolo+Tab girano sui suoi controlli e non
 *     escono;
 *   - Esc la chiude;
 *   - alla chiusura il fuoco torna al tasto che l'aveva aperta, cosi' chi
 *     usa la tastiera riprende da dove era.
 *
 * COSA NON FA: non blocca lo scorrimento della pagina e non disegna lo
 * sfondo scuro; quello lo fa chi la disegna.
 */
import {useEffect, useRef} from 'react';

const FOCALIZZABILI = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/** I controlli di un contenitore che prendono il fuoco col Tab, in ordine. */
export function controlliDi(contenitore) {
  if (!contenitore) return [];
  return Array.from(contenitore.querySelectorAll(FOCALIZZABILI))
    .filter((el) => el.getAttribute('aria-hidden') !== 'true');
}

/**
 * @param ref       il contenitore con role="dialog"
 * @param onChiudi  chiude la finestra (Esc)
 * @param attivo    false per chi a volte non e' una finestra (il foglio dentro
 *                  la prenotazione): allora non fa niente
 */
export default function useDialogo(ref, onChiudi, attivo = true) {
  const chiudi = useRef(onChiudi);
  chiudi.current = onChiudi;

  useEffect(() => {
    if (!attivo) return undefined;
    const prima = typeof document !== 'undefined' ? document.activeElement : null;
    const box = ref.current;
    const primo = controlliDi(box)[0];
    if (primo) primo.focus();
    else if (box) box.focus();

    const tasto = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (chiudi.current) chiudi.current();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const tutti = controlliDi(ref.current);
      if (!tutti.length) {
        e.preventDefault();
        return;
      }
      const inizio = tutti[0];
      const fine = tutti[tutti.length - 1];
      const dentro = ref.current.contains(document.activeElement);
      if (e.shiftKey && (!dentro || document.activeElement === inizio)) {
        e.preventDefault();
        fine.focus();
      } else if (!e.shiftKey && (!dentro || document.activeElement === fine)) {
        e.preventDefault();
        inizio.focus();
      }
    };
    window.addEventListener('keydown', tasto);
    return () => {
      window.removeEventListener('keydown', tasto);
      /* Il fuoco torna a chi aveva aperto, se c'e' ancora. */
      if (prima && typeof prima.focus === 'function' && document.body.contains(prima)) {
        try { prima.focus(); } catch (e) { /* sparito nel frattempo */ }
      }
    };
  }, [ref, attivo]);
}
